import { currencyForCountry, formatReportMoney, reportCurrency } from '@evi/shared';
import type { AgentOpsQueueRow } from './api';

/**
 * Sales Partner commission money, one currency at a time (2026-10-16).
 *
 * Warren: a Canadian partner sees CAD, not USD, and a partner with both
 * currencies sees each one separately, never combined. Every commission row
 * carries its own currency from the Api (the AgentPayout's, else its
 * session's cost.currency). Nothing here adds two currencies into one
 * number and nothing assumes USD: a row with no recorded currency keeps a
 * line of its own. Same grouping as payoutPageTotals in reportTotals.ts.
 */

/** A commission row as GET /admin/agents/:id/payouts sends it. Declared
 *  here rather than in api.ts; `currency` is optional because an Api from
 *  before 2026-10-16 omits it. */
export type CommissionRow = AgentOpsQueueRow & { currency?: string | null };

/** One currency's commission in a slice of rows. */
export interface CommissionTotal { currency: string | null; cents: number; count: number }

/** One chart: a single currency's buckets, oldest first, in major units.
 *  `currencies` is empty only on the placeholder shown before a partner has
 *  any commission row or a payout account. */
export interface CommissionChart {
  key: string;
  title: string;
  currencies: ReadonlyArray<string | null>;
  data: number[];
}

const byCode = (a: string | null, b: string | null): number => (a ?? '').localeCompare(b ?? '');

/** A row's own currency, uppercase, or null when none is recorded. */
export function rowCurrency(row: CommissionRow): string | null {
  return reportCurrency(row.currency);
}

/** Sums each currency on its own, sorted by currency code. */
export function commissionTotals(
  rows: readonly CommissionRow[],
  include: (row: CommissionRow) => boolean = () => true,
): CommissionTotal[] {
  const totals = new Map<string | null, CommissionTotal>();
  for (const row of rows) {
    if (!include(row)) continue;
    const currency = rowCurrency(row);
    const total = totals.get(currency) ?? { currency, cents: 0, count: 0 };
    total.cents += row.gross_commission_cents;
    total.count += 1;
    totals.set(currency, total);
  }
  return [...totals.values()].sort((a, b) => byCode(a.currency, b.currency));
}

/** The currencies a partner's figures are shown in: every currency their
 *  commission rows carry; with no rows yet, the payout account's currency
 *  (payout_country is null until an account exists); otherwise none. */
export function commissionCurrencies(
  rows: readonly CommissionRow[],
  payoutCountry: string | null | undefined,
): Array<string | null> {
  const present = [...new Set(rows.map(rowCurrency))].sort(byCode);
  if (present.length > 0) return present;
  return payoutCountry === 'US' || payoutCountry === 'CA' ? [currencyForCountry(payoutCountry)] : [];
}

/** One amount per currency, joined: "CA$12.00 · US$3.00". A currency with
 *  nothing in the slice reads as zero in that currency. */
export function formatCommissionTotals(
  totals: readonly CommissionTotal[],
  currencies: ReadonlyArray<string | null>,
): string {
  if (currencies.length === 0) return 'No commission yet';
  return currencies
    .map((c) => formatReportMoney(totals.find((t) => t.currency === c)?.cents ?? 0, c))
    .join(' · ');
}

/** Average commission per session, each currency over its own sessions. */
export function formatCommissionAverage(totals: readonly CommissionTotal[]): string {
  const counted = totals.filter((t) => t.count > 0);
  if (counted.length === 0) return 'No sessions yet';
  return counted.map((t) => formatReportMoney(t.cents / t.count, t.currency)).join(' · ');
}

/** One row's amount (gross by default) in that row's own currency. A row
 *  with no recorded currency shows the bare amount, marked, never as USD. */
export function formatCommissionRow(row: CommissionRow, cents: number = row.gross_commission_cents): string {
  const currency = rowCurrency(row);
  return currency ? formatReportMoney(cents, currency) : `${(cents / 100).toFixed(2)} (currency not recorded)`;
}

/** One chart per currency. `bucketOf` maps a row to its bucket index; an
 *  index outside 0 to length minus 1 is outside the window. */
export function commissionCharts(
  rows: readonly CommissionRow[],
  currencies: ReadonlyArray<string | null>,
  length: number,
  bucketOf: (row: CommissionRow) => number,
): CommissionChart[] {
  if (currencies.length === 0) {
    return [{ key: 'none', title: 'COMMISSION', currencies: [], data: new Array<number>(length).fill(0) }];
  }
  return currencies.map((currency) => {
    const cents = new Array<number>(length).fill(0);
    for (const row of rows) {
      if (rowCurrency(row) !== currency) continue;
      const i = bucketOf(row);
      if (i >= 0 && i < length) cents[i] = (cents[i] ?? 0) + row.gross_commission_cents;
    }
    return {
      key: currency ?? 'not-recorded',
      title: `COMMISSION: ${currency ?? 'NOT RECORDED'}`,
      currencies: [currency],
      data: cents.map((c) => c / 100),
    };
  });
}

/** Axis labels: "$" for USD only. Any other currency prints the number and
 *  the chart title names it (the axis gutter is too narrow for "CA$100"). */
export function commissionAxis(chart: CommissionChart): (n: number) => string {
  return chart.currencies[0] === 'USD' ? (n) => `$${Math.round(n)}` : (n) => String(Math.round(n));
}

/** A chart footer: that chart's currency total and session count. */
export function commissionChartFooter(chart: CommissionChart, totals: readonly CommissionTotal[]) {
  const total = chart.currencies.length > 0
    ? totals.find((t) => t.currency === chart.currencies[0])
    : undefined;
  return {
    amount: (total?.cents ?? 0) / 100,
    label: formatCommissionTotals(totals, chart.currencies),
    count: total?.count ?? 0,
  };
}
